/* eslint-disable react/no-unescaped-entities */
import React from 'react'
import Layout from '../../components/BridgePage/Layout'
import './index.scss'

const AboutPpX = () => (
  <Layout>
    <div className='aboutPp'>
      <h1 className='ppTitle'>Yaaro Privacy Policy</h1>
      <p><strong>Effective Date:</strong> May 15, 2026</p>
      <p>
        This Privacy Policy explains how Yaaro collects, uses, and protects your personal information,
        the choices you have regarding your data, the measures we take to safeguard minors' privacy,
        and how this policy may be updated. Before using our services, please read this entire
        Privacy Policy carefully.
      </p>
      <p>
        This Privacy Policy applies to both the standard Yaaro application and Yaaro (together
        referred to as the "Services"). If a particular feature described in this policy is
        not available in Yaaro, then the associated data collection and use do not occur in
        Yaaro.
      </p>

      <h2>I. What Data Do We Collect?</h2>
      <p>
        With your explicit consent and as required for the proper functioning of the application,
        Yaaro collects, stores, and processes the following categories of data:
      </p>
      <ul>
        <li>
          <strong>Network Permission</strong>
          <ul>
            <li>Purpose: Allows the app to access the internet for data transmission.</li>
            <li>
              Reason: Required to connect to remote servers and enable communication or data
              retrieval.
            </li>
          </ul>
        </li>
        <li>
          <strong>Wi-Fi State Permission</strong>
          <ul>
            <li>Purpose: Allows the app to detect Wi-Fi connection details and status.</li>
            <li>Reason: Supports features that depend on Wi-Fi availability and connection quality.</li>
          </ul>
        </li>
        <li>
          <strong>Network Status Permission</strong>
          <ul>
            <li>Purpose: Permits access to network connectivity status.</li>
            <li>Reason: Helps the app perform efficiently by monitoring network availability.</li>
          </ul>
        </li>
        <li>
          <strong>Modify Network State Permission</strong>
          <ul>
            <li>Purpose: Grants the ability to adjust network settings.</li>
            <li>Reason: Enhances service stability and continuity.</li>
          </ul>
        </li>
        <li>
          <strong>Change Wi-Fi State Permission</strong>
          <ul>
            <li>Purpose: Allows toggling Wi-Fi connections on and off.</li>
            <li>Reason: Optimises app operations based on connectivity needs.</li>
          </ul>
        </li>
        <li>
          <strong>Camera Permission (No Data Collection)</strong>
          <ul>
            <li>Purpose: Provides access to the device camera.</li>
            <li>Reason: Enables features such as taking photos, without storing any personal image data.</li>
          </ul>
        </li>
        <li>
          <strong>Microphone Permission (No Data Collection)</strong>
          <ul>
            <li>Purpose: Provides access to the device microphone.</li>
            <li>Reason: Supports voice input and interactions without collecting or saving user audio.</li>
          </ul>
        </li>
        <li>
          <strong>Change Audio Settings Permission</strong>
          <ul>
            <li>Purpose: Permits modification of global audio settings.</li>
            <li>
              Reason: Improves audio quality and user experience (e.g., switching between speaker and
              headphone modes).
            </li>
          </ul>
        </li>
        <li>
          <strong>Notification Bar Usage Permission</strong>
          <ul>
            <li>Purpose: Allows notifications and updates to appear in the device's notification bar.</li>
            <li>Reason: Ensures timely delivery of important information and updates.</li>
          </ul>
        </li>
      </ul>

      <h2>II. How Do We Collect Your Data?</h2>
      <p>Yaaro collects your personal information for the following purposes:</p>
      <ul>
        <li>To ensure the proper operation of the application and related services.</li>
        <li>
          To provide services to you, including verification, upgrades, synchronisation, and (where
          applicable) advertising.
        </li>
        <li>To perform user authentication.</li>
        <li>To facilitate account recovery processes.</li>
        <li>For archiving and data backup.</li>
        <li>To enhance user data security and deliver customer support.</li>
        <li>To ensure security, prevent fraud, and address potential threats.</li>
        <li>To provide specific software features based on your explicit requests.</li>
        <li>
          To conduct research and analysis regarding your use of the services, as well as
          troubleshooting, maintenance, and service quality improvement.
        </li>
        <li>To send you marketing communications (you may opt out at any time).</li>
        <li>
          To comply with legal obligations, including the monitoring of user-generated content (e.g.,
          comments, images, and voice messages) to ensure it does not contain pornographic,
          infringing, hateful, violent, or otherwise unlawful material.
        </li>
      </ul>
      <p>
        For Yaaro, where a feature is not offered, the corresponding collection and processing
        described in this section do not occur.
      </p>

      <h3>Sharing of Personal Information</h3>
      <p>Yaaro may share your personal information in the following circumstances:</p>
      <ul>
        <li>
          <strong>Service Providers and Partners:</strong> Yaaro may share personal data with
          service providers acting on its behalf or with partners, in accordance with your directions.
          Yaaro does not disclose personal data to third parties for their own independent marketing
          purposes.
        </li>
        <li>
          <strong>Affiliates:</strong> Personal information may be shared within Yaaro's corporate
          group (including subsidiaries, parent companies, and other affiliates) as well as with
          strategic business partners, whether located domestically or internationally, for the
          purposes outlined above.
        </li>
        <li>
          <strong>Third-Party Service Providers:</strong> Yaaro may engage third-party providers to
          perform specific functions on its behalf, such as data processing, fraud prevention, or
          payment services. These providers are contractually bound to process personal information in
          compliance with this Privacy Policy and Yaaro's instructions. They are prohibited from using
          the information for their own purposes and must delete or return the data upon completing
          the assigned tasks.
        </li>
        <li>
          <strong>Public Authorities:</strong> Yaaro may disclose personal information to law
          enforcement, public authorities, tax agencies, or other organisations if required by law or
          when Yaaro reasonably determines such disclosure is necessary for national security, public
          safety, or compliance with applicable regulations.
        </li>
        <li>
          <strong>Corporate Successors:</strong> In the event of a corporate transaction (e.g.,
          merger, acquisition, sale of assets, or partnership), Yaaro may transfer personal
          information as part of the transaction. Personal data may constitute a transferred asset in
          such cases.
        </li>
        <li>
          <strong>With Your Consent or Direction:</strong> Yaaro may share personal information based
          on your explicit consent or instructions.
        </li>
        <li>
          <strong>Legal and Operational Necessity:</strong> Yaaro may disclose personal information to
          enforce its terms of service, protect its operational integrity, or safeguard the interests
          of its users, provided such disclosure has a lawful basis.
        </li>
      </ul>

      <h2>III. How Will We Use Your Data?</h2>
      <p>Yaaro processes the personal information it collects for the following purposes:</p>
      <ul>
        <li>To ensure the effective operation of the app and associated services.</li>
        <li>
          To deliver services, including verification, synchronisation, upgrades, and (where
          applicable) advertising.
        </li>
        <li>To perform user authentication and account recovery procedures.</li>
        <li>To archive and back up data securely.</li>
        <li>To enhance the safety of user information and provide customer support.</li>
        <li>To conduct research, troubleshooting, maintenance, and service improvement.</li>
        <li>
          To send marketing communications tailored to your preferences (you may opt out at any time).
        </li>
        <li>
          To comply with legal obligations, including the monitoring of user-generated content for
          compliance with applicable laws (e.g., removal of pornographic, hate-related, violent,
          infringing, or otherwise illegal materials).
        </li>
      </ul>
      <p>
        For Yaaro, if a listed purpose depends on a feature not present in Yaaro, the related
        processing does not occur.
      </p>

      <h3>Sharing of Personal Data (Summary):</h3>
      <ul>
        <li>
          <strong>With Affiliates:</strong> Your information may be shared with entities within our
          corporate group, including subsidiaries and strategic partners, for the purposes outlined
          above.
        </li>
        <li>
          <strong>With Service Providers:</strong> Third-party providers may handle tasks on our behalf
          (e.g., fraud prevention, payment processing, data storage) under strict confidentiality
          obligations.
        </li>
        <li>
          <strong>With Public Authorities:</strong> Information may be disclosed to law enforcement or
          governmental bodies when legally mandated.
        </li>
        <li>
          <strong>With Successors:</strong> Your data may be transferred as part of a merger,
          acquisition, or similar business transaction.
        </li>
        <li>
          <strong>At Your Direction or Consent:</strong> Information may be disclosed with your
          explicit approval.
        </li>
      </ul>

      <h2>IV. How Do We Store and Protect Your Data?</h2>
      <p>
        We store your personal information securely in Singapore. The retention period for personal
        information is determined in compliance with applicable legal requirements (e.g., commercial
        and tax-related retention periods). Once these periods expire, the relevant information will
        be systematically deleted unless it is still needed for the fulfilment or initiation of a
        contract, or there remains a legitimate interest in retaining it.
      </p>
      <p>
        Yaaro implements robust security measures in accordance with industry standards to safeguard
        your personal information. These measures are designed to protect against unauthorised access,
        unauthorised disclosure, misuse, alteration, destruction, or loss of data. While we strive to
        maintain high standards of security, please be aware that the internet is not entirely
        secure. Communication methods such as email, instant messaging, or interactions with other
        users are not encrypted. We strongly advise against sharing personal information through such
        channels. To enhance the security of your account, we recommend using a strong and unique
        password.
      </p>

      <h2>V. How Does the App Use Device Permissions?</h2>
      <p>
        When providing services to you, we may need to request certain device permissions to collect
        the information described above and enable related functions. These permissions are not granted
        by default; they will only be activated after you provide explicit authorisation for specific
        features or services. You may withdraw your consent and deny these permissions at any time.
        Importantly, even if we have obtained your consent for sensitive permissions in the past, we
        will not collect your information when the relevant functions or services are not required.
      </p>
      <p>
        Additionally, please note that we may integrate certain third-party SDKs to support the
        security and operation of our products. You may also use third-party developed H5 pages or
        mini-programs within Yaaro, which may request relevant operating system permissions when in
        use.
      </p>

      <h3>Permissions That May Be Required</h3>

      <h4>1. Network and Device State Permissions</h4>
      <ul>
        <li>
          <strong>android.permission.INTERNET</strong>
          <ul>
            <li>Purpose: Allows the app to access the internet and transmit data.</li>
            <li>
              Reason: The app needs to connect to the internet to retrieve remote data or send requests.
            </li>
          </ul>
        </li>
        <li>
          <strong>android.permission.ACCESS_NETWORK_STATE</strong>
          <ul>
            <li>Purpose: Allows the app to view network connection status (e.g., whether Wi-Fi is connected).</li>
            <li>Reason: Used to optimise network requests or determine network availability.</li>
          </ul>
        </li>
        <li>
          <strong>android.permission.ACCESS_WIFI_STATE</strong>
          <ul>
            <li>Purpose: Allows the app to check Wi-Fi status and details.</li>
            <li>
              Reason: Used to detect whether the device is connected via Wi-Fi and assess Wi-Fi signal
              strength.
            </li>
          </ul>
        </li>
      </ul>

      <h4>2. Audio and Media Permissions</h4>
      <ul>
        <li>
          <strong>android.permission.RECORD_AUDIO</strong>
          <ul>
            <li>Purpose: Allows the app to record audio.</li>
            <li>
              Reason: Used for voice input, recording functionality, or audio capture (e.g., users in
              live voice rooms require authorisation to speak or hear others).
            </li>
          </ul>
        </li>
        <li>
          <strong>android.permission.MODIFY_AUDIO_SETTINGS</strong>
          <ul>
            <li>
              Purpose: Allows the app to modify global audio settings (e.g., adjust volume or switch
              audio modes).
            </li>
            <li>
              Reason: Enhances the user audio experience, such as switching between speaker and
              headphone modes.
            </li>
          </ul>
        </li>
      </ul>

      <h4>3. Storage and Media Permissions</h4>
      <ul>
        <li>
          <strong>android.permission.READ_EXTERNAL_STORAGE (Android 13 and below)</strong>
          <ul>
            <li>Purpose: Allows the app to read external storage on the device (e.g., images, videos, documents).</li>
            <li>Reason: Used to access files or media stored on the device.</li>
          </ul>
        </li>
        <li>
          <strong>android.permission.WRITE_EXTERNAL_STORAGE</strong>
          <ul>
            <li>Purpose: Allows the app to write to external storage on the device.</li>
            <li>Reason: Used to save files or media data to the device.</li>
          </ul>
        </li>
        <li>
          <strong>android.permission.READ_MEDIA_AUDIO (Android 13 and above)</strong>
          <ul>
            <li>Purpose: Allows the app to read audio files from the device.</li>
            <li>Reason: Specifically used on Android 13 and above to access audio files.</li>
          </ul>
        </li>
        <li>
          <strong>android.permission.CAMERA</strong>
          <ul>
            <li>Purpose: Allows the app to access the camera for taking pictures.</li>
            <li>Reason: Used to enable features like setting a custom profile photo.</li>
          </ul>
        </li>
      </ul>

      <h4>4. Notification and Foreground Service Permissions</h4>
      <ul>
        <li>
          <strong>android.permission.POST_NOTIFICATIONS (Android 13 and above)</strong>
          <ul>
            <li>Purpose: Allows the app to send notifications to the user.</li>
            <li>Reason: Necessary for displaying notifications.</li>
          </ul>
        </li>
        <li>
          <strong>android.permission.FOREGROUND_SERVICE</strong>
          <ul>
            <li>Purpose: Allows the app to run foreground services.</li>
            <li>
              Reason: Ensures important tasks (e.g., audio playback, network operations) continue
              running even in the background.
            </li>
          </ul>
        </li>
      </ul>

      <h4>5. Billing-Related Permissions</h4>
      <ul>
        <li>
          <strong>com.android.vending.BILLING</strong>
          <ul>
            <li>Purpose: Allows the app to use Google Play in-app purchase functionality.</li>
            <li>Reason: Supports in-app purchases or subscription services.</li>
          </ul>
        </li>
      </ul>

      <h2>VI. Marketing Communications</h2>
      <p>
        Yaaro may send you marketing communications related to our services or those of our partners.
        You may opt out at any time by contacting us at <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>.
      </p>

      <h2>VII. What Are Your Data Protection Rights?</h2>
      <p>Under applicable laws, you have the following rights:</p>
      <ul>
        <li>
          <strong>Right to Access:</strong> Obtain confirmation of whether your data is processed and
          request access to your data.
        </li>
        <li>
          <strong>Right to Rectification:</strong> Correct inaccuracies or complete incomplete data.
        </li>
        <li>
          <strong>Right to Erasure:</strong> Request the deletion of your personal data.
        </li>
        <li>
          <strong>Right to Restriction of Processing:</strong> Limit the processing of your data under
          specific conditions.
        </li>
        <li>
          <strong>Right to Data Portability:</strong> Receive your data in a structured,
          machine-readable format for transfer to another service provider.
        </li>
        <li>
          <strong>Right to Object:</strong> Object to the processing of your data for legitimate
          interests or public tasks.
        </li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>.
      </p>

      <h2>VIII. Third-Party SDK Usage</h2>
      <p>
        To provide our services effectively, Yaaro integrates third-party SDK technologies into our
        platform. When you use third-party-developed web pages or applications, these external parties
        may implement their own cookies or other similar technical tracking tools. Yaaro exercises no
        control over the operation of these cookies or tools, and this Privacy Policy does not apply to
        or regulate their usage. To better safeguard your personal information, we advise you to review
        the privacy policies of all third-party SDK providers prior to using their related services. If
        you detect any potential risks linked to these SDKs or comparable tools, we recommend that you
        immediately stop using the relevant services and contact us for further support.
      </p>
      <p>
        To enhance our services, improve service stability, optimize performance, or introduce new
        features, we may update or adjust the third-party SDKs we utilize. All such adjustments shall
        strictly comply with applicable laws, regulations and regulatory requirements. Before integrating
        any third-party SDK, we assess the lawfulness, legitimacy and necessity of its personal data
        collection to protect your privacy. You can check the data usage and protection rules of third
        parties through the relevant links we provide. Please note that third-party SDKs may change
        their personal data handling practices due to version updates, policy revisions or other
        factors. For the latest details, please refer to the <a href="/yaaro/about/thirdPartySDK">"Third-Party SDK List"</a> available within the
        app.
      </p>

      <h2>IX. Privacy Policy for Minors</h2>
      <p>
        Yaaro will not knowingly collect, request, or retain personal information from individuals who
        are under the age of 18 (or the applicable minimum age of consent in your jurisdiction)
        ("Minors"). If you are a Minor, please do not provide Yaaro with any personal
        information, including your name, address, phone number, or email address. If we discover that
        personal information from a Minor has been collected, we will promptly delete such data. If you
        suspect that Yaaro may have inadvertently collected information from or about a Minor, please
        contact us at:
      </p>
      <p>
        Email: <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>
      </p>

      <h2>X. No Sale of Personal Information</h2>
      <p>
        To comply with the California Consumer Privacy Act (CCPA), Yaaro hereby sets forth the following
        commitments with respect to your personal information:
      </p>
      <ul>
        <li>
          Yaaro will only collect, use, or disclose personal data for the purposes specified in this
          Privacy Policy or as otherwise permitted by the CCPA.
        </li>
        <li>
          Under no circumstances will Yaaro sell your personal information to third parties for
          independent use or marketing purposes.
        </li>
        <li>
          Any data shared with service providers or partners (e.g., for payment processing, fraud
          prevention, or service delivery) will be done under strict confidentiality agreements,
          ensuring it is not used for unauthorised purposes.
        </li>
      </ul>

      <h2>XI. Cross-Border Data Transfers</h2>
      <p>
        Yaaro operates globally. To provide seamless services, your personal information may be
        transferred to or accessed by entities in other countries. Yaaro complies with applicable data
        transfer regulations to ensure the security of your information regardless of its location.
      </p>
      <p>
        For users located in the European Economic Area (EEA), the United Kingdom, or Switzerland, please
        note that your personal data may be processed outside these jurisdictions. Such data transfers
        are governed by one of the following mechanisms:
      </p>
      <ul>
        <li>Standard Contractual Clauses approved by the European Commission or other relevant authorities.</li>
        <li>
          Adequacy decisions issued by the European Commission or equivalent authorities confirming that
          the destination country provides an adequate level of data protection.
        </li>
        <li>
          Other legal mechanisms specific to data transfers to the United States or other regions in
          compliance with applicable laws.
        </li>
      </ul>

      <h2>XII. Privacy Policies of Other Websites</h2>
      <p>
        Yaaro's services may include links to external websites or applications. This Privacy Policy
        applies exclusively to Yaaro's services. If you interact with third-party websites or
        applications, we strongly advise reviewing their respective privacy policies to understand how
        they manage your personal data.
      </p>
      <p>
        If you reside in Virginia, you are entitled to privacy rights under the Virginia Consumer Data
        Protection Act (VCDPA), including the right to access, correct, or delete your personal data,
        as well as to opt out of specific data processing activities. To exercise these rights, please
        contact us at <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>. When submitting an
        opt-out request, please specify the type of request to enable efficient processing. For
        security purposes, we may request additional information to verify your identity. Yaaro is
        committed to respecting your preferences and will process opt-out requests in accordance with
        the VCDPA.
      </p>

      <h2>XIII. Updates to This Policy</h2>
      <p>
        Yaaro regularly reviews its Privacy Policy and publishes updates on this page. When we make
        material changes, we will revise the "Effective Date" at the top of this policy and
        notify you through the platform or other appropriate channels.
      </p>

      <h2>XIV. Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy Policy or your personal data,
        contact us at:
      </p>
      <p>
        Email: <a href="mailto:report@yaarolive.com">report@yaarolive.com</a>
      </p>
      <p>
        If you are unsatisfied with our response, you may contact the relevant data protection authority
        in your jurisdiction.
      </p>
    </div>
  </Layout>
)

export default AboutPpX
